import { useCallback, useEffect, useRef, useState } from 'react'

import { showError } from '../utils/notifications.js'

// EVA-138: shared "paste overflow" mechanism for Handsontable grids.
//
// When pasted data has more rows than the grid, Handsontable silently drops
// the excess. This hook cancels the native paste, asks the server to create
// the missing rows, refetches, then replays the paste over the full grid and
// flushes immediately so a concurrent refetch can't wipe the values.
//
// The hook owns the paste lifecycle; the component supplies table-specific
// behavior via callbacks (kept in a latest-value ref, so they may be
// re-created on every render):
//   createRows(count)      -> Promise<boolean> — create N blank rows server-side
//   refetchData()          -> Promise — silent refetch (must keep HotTable mounted)
//   flushNow()             -> Promise — cancel any debounce and flush pending changes
//   getAnchorId(visualRow) -> id of the row the paste starts at (or undefined)
//   resolveAnchorRow(id)   -> visual row index for that id after refetch
//
// Returns { handleBeforePaste, creatingRowsRef }. Wire handleBeforePaste into
// the HotTable `beforePaste` setting; use creatingRowsRef to suppress
// ActionCable-driven refetches while a paste is in flight.
export default function usePasteOverflow({
  hotRef, enabled, createRows, refetchData, flushNow, getAnchorId, resolveAnchorRow
}) {
  const pendingPasteRef = useRef(null)
  const creatingRowsRef = useRef(false)
  const [replayTrigger, setReplayTrigger] = useState(0)

  const cbRef = useRef({})
  cbRef.current = { enabled, createRows, refetchData, flushNow, getAnchorId, resolveAnchorRow }

  const handleBeforePaste = useCallback((pasteData, coords) => {
    if (!cbRef.current.enabled) return false
    const hot = hotRef.current?.hotInstance
    if (!hot) return false

    const { startRow, startCol } = coords[0]
    const overflowCount = (startRow + pasteData.length) - hot.countSourceRows()
    if (overflowCount <= 0) return // fits — let the native paste proceed
    if (creatingRowsRef.current) return false // a paste is already in flight

    creatingRowsRef.current = true
    pendingPasteRef.current = {
      data: pasteData.map(row => [...row]),
      startRow,
      startCol,
      anchorId: cbRef.current.getAnchorId(startRow)
    }

    ;(async () => {
      try {
        const ok = await cbRef.current.createRows(overflowCount)
        if (!ok) throw new Error('row creation failed')
        await cbRef.current.refetchData()
        // Replay runs in the effect below, after React commits the new rows.
        setReplayTrigger(prev => prev + 1)
      } catch (e) {
        console.error('Paste overflow row creation failed:', e)
        showError('Failed to create rows for paste')
        pendingPasteRef.current = null
        creatingRowsRef.current = false
      }
    })()

    return false // cancel the native paste
  }, [hotRef])

  // Replay the paste after new rows are created and loaded
  useEffect(() => {
    if (!pendingPasteRef.current) return
    const hot = hotRef.current?.hotInstance
    if (!hot) {
      pendingPasteRef.current = null
      creatingRowsRef.current = false
      return
    }

    const { data, startRow, startCol, anchorId } = pendingPasteRef.current
    pendingPasteRef.current = null

    const resolvedStartRow = anchorId != null
      ? (cbRef.current.resolveAnchorRow(anchorId) ?? startRow)
      : startRow

    requestAnimationFrame(() => {
      try {
        hot.populateFromArray(resolvedStartRow, startCol, data, undefined, undefined, 'edit', 'overwrite')
      } catch (e) {
        console.error('Paste overflow replay failed:', e)
      }

      // HOT 18's React wrapper batches the state update triggered by afterChange.
      // Wait for the next frame so table-specific pending refs have been filled
      // before flushNow reads them; calling it in populateFromArray's finally block
      // races the React updater and leaves the replay visible but unsaved.
      requestAnimationFrame(() => {
        Promise.resolve(cbRef.current.flushNow())
          .finally(() => { creatingRowsRef.current = false })
      })
    })
  }, [replayTrigger, hotRef])

  return { handleBeforePaste, creatingRowsRef }
}
